import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, TrendingUp, MapPin, Layers, Zap, ArrowRight } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import StatusPill from "@/components/marketplace/StatusPill";
import StripeCheckoutPanel from "@/components/marketplace/StripeCheckoutPanel";

const KINDS = [
  { key: "homepage_feature", label: "Homepage feature", desc: "Rotates in the coveted homepage carousel.", icon: TrendingUp },
  { key: "category_feature", label: "Category feature", desc: "Top of a specific category page.", icon: Layers, requires: "category_id" },
  { key: "city_feature", label: "City feature", desc: "Top spot in a Gulf Coast city.", icon: MapPin, requires: "city_id" },
  { key: "search_priority_boost", label: "Search boost", desc: "Priority ranking across search results.", icon: Zap },
];

export default function FeaturedPlacementPage() {
  const [pricing, setPricing] = useState({});
  const [placements, setPlacements] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ kind: "homepage_feature", weeks: 1, service_id: "", category_id: "", city_id: "" });
  const [pending, setPending] = useState(null);
  useDocumentTitle("Featured Placement");

  const load = async () => {
    try {
      const [p, mine, svcs, cats, ct] = await Promise.all([
        api.get("/featured/pricing"),
        api.get("/featured/mine"),
        api.get("/services/mine/list"),
        api.get("/categories"),
        api.get("/locations", { params: { level: "city", limit: 5000 } }),
      ]);
      setPricing(p.data || {});
      setPlacements(mine.data || []);
      setServices((svcs.data || []).filter((s) => s.status === "published"));
      setCategories(cats.data || []);
      setCities(ct.data || []);
    } catch (err) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (services.length && !form.service_id) setForm((f) => ({ ...f, service_id: services[0].id }));
  }, [services, form.service_id]);

  const weekly = Number(pricing[form.kind] || 0);
  const total = weekly * (Number(form.weeks) || 1);

  const create = async () => {
    try {
      const payload = {
        kind: form.kind,
        service_id: form.service_id,
        weeks: Number(form.weeks),
        category_id: form.kind === "category_feature" ? form.category_id : null,
        city_id: form.kind === "city_feature" ? form.city_id : null,
      };
      const { data } = await api.post("/featured", payload);
      setPending(data);
      toast.success("Placement created — proceed to secure payment.");
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Cannot create placement"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Featured Placement</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Boost your service in front of Gulf Coast customers</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">Test-mode Stripe checkout for now — activation is instant on paid.</p>
        </div>

        {/* Kind selector */}
        <div className="grid gap-3 sm:grid-cols-4 mb-6">
          {KINDS.map((k) => (
            <button key={k.key} onClick={() => setForm({ ...form, kind: k.key })} className={`text-left rounded-2xl border p-4 hover:border-[#165a71]/40 ${form.kind === k.key ? "border-[#165a71] bg-[#165a71]/5" : "border-slate-200"}`} data-testid={`fp-kind-${k.key}`}>
              <div className="inline-flex h-8 w-8 rounded-lg bg-[#165a71]/10 items-center justify-center text-[#165a71] mb-2"><k.icon className="h-4 w-4" /></div>
              <div className="font-display font-semibold text-[#071b22]">{k.label}</div>
              <div className="text-xs text-[#252A34]/70 mt-1">{k.desc}</div>
              <div className="text-sm text-[#252A34] mt-2 font-medium">${pricing[k.key] || 0}/wk</div>
            </button>
          ))}
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6" data-testid="fp-form">
          <div className="grid gap-3 sm:grid-cols-3">
            <F label="Service">
              <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })} className="fp-input" data-testid="fp-service">
                {services.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </F>
            <F label="Weeks">
              <input type="number" min={1} max={26} value={form.weeks} onChange={(e) => setForm({ ...form, weeks: e.target.value })} className="fp-input" data-testid="fp-weeks" />
            </F>
            {form.kind === "category_feature" && (
              <F label="Category">
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="fp-input" data-testid="fp-category">
                  <option value="">Select…</option>
                  {categories.filter((c) => !c.parent_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </F>
            )}
            {form.kind === "city_feature" && (
              <F label="City">
                <select value={form.city_id} onChange={(e) => setForm({ ...form, city_id: e.target.value })} className="fp-input" data-testid="fp-city">
                  <option value="">Select…</option>
                  {cities.map((c) => <option key={c.id} value={c.id}>{c.name}, {c.state_code}</option>)}
                </select>
              </F>
            )}
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Total</div>
              <div className="font-display text-2xl font-semibold text-[#071b22]">${total.toFixed(2)}</div>
            </div>
            <button onClick={create} disabled={!form.service_id} className="rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#11485a] disabled:opacity-60" data-testid="fp-create">
              Create placement
            </button>
          </div>
        </div>

        {pending && pending.status === "payment_pending" && (
          <div className="mb-6">
            <StripeCheckoutPanel purpose="featured_placement" targetId={pending.id} amount={pending.total_price} ctaLabel="Pay with card" testId="fp-checkout-panel" />
          </div>
        )}

        {/* My placements */}
        <div>
          <h2 className="font-display text-xl font-semibold text-[#071b22] mb-3">My placements</h2>
          {placements.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-[#252A34]/70">You don't have any placements yet.</div>
          ) : (
            <div className="grid gap-3" data-testid="fp-list">
              {placements.map((p) => (
                <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 flex items-center gap-4" data-testid={`fp-row-${p.id}`}>
                  <div className="h-12 w-12 rounded-xl bg-[#165a71]/10 flex items-center justify-center text-[#165a71]"><Sparkles className="h-5 w-5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[#071b22] capitalize">{p.kind.replace(/_/g, " ")}</div>
                    <div className="text-xs text-[#252A34]/60 mt-1">${p.total_price.toFixed(2)} for {p.weeks} week(s) · {p.impressions || 0} impressions · {p.clicks || 0} clicks</div>
                  </div>
                  <StatusPill status={p.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <style>{`
        .fp-input { width: 100%; border-radius: 0.75rem; border: 1px solid #e2e8f0; padding: 0.5rem 0.75rem; font-size: 0.875rem; background: white; }
      `}</style>
    </DashboardShell>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-medium text-[#252A34]/70 mb-1">{label}</div>
    {children}
  </label>
);
